import React, { useState } from "react";
import { Mail, MapPin, Clock, Send, MessageSquare, CheckCircle2 } from "lucide-react";
import { useToast } from "../hooks/use-toast";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const Contact = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", email: "", topic: "general", message: "" });
  const [sent, setSent] = useState(false);

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({ title: "Please complete all fields", variant: "destructive" });
      return;
    }
    setSent(true);
    toast({ title: "Message sent", description: "We'll respond within 1 business day." });
    setForm({ name: "", email: "", topic: "general", message: "" });
  };

  return (
    <div>
      <section className="relative bg-gradient-to-b from-blue-50 to-white border-b border-slate-200">
        <div className="absolute inset-0 fesx-grid-bg opacity-60" />
        <div className="relative mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-14 text-center">
          <h1 className="font-display font-extrabold text-4xl text-slate-900">Get in Touch</h1>
          <p className="mt-3 text-lg text-slate-600">Questions about products, CoAs or an order? Our team responds within 1 business day.</p>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid lg:grid-cols-5 gap-8">
          {/* info */}
          <div className="lg:col-span-2 space-y-4">
            {[
              { icon: Mail, t: "Email Us", s: "support@fesxpeptides.com.au" },
              { icon: MapPin, t: "Location", s: "Dispatch centre · Australia-wide" },
              { icon: Clock, t: "Support Hours", s: "Mon–Fri, 9am–5pm AEST" },
            ].map((c) => (
              <div key={c.t} className="flex items-start gap-4 rounded-2xl border border-slate-200 bg-white p-5">
                <div className="h-11 w-11 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                  <c.icon className="h-5 w-5" />
                </div>
                <div>
                  <div className="font-bold text-slate-900">{c.t}</div>
                  <div className="text-sm text-slate-500 mt-0.5">{c.s}</div>
                </div>
              </div>
            ))}
            <div className="rounded-2xl bg-blue-600 text-white p-6">
              <MessageSquare className="h-7 w-7 text-blue-200" />
              <h3 className="mt-3 font-display font-bold text-lg">Research Use Only</h3>
              <p className="mt-1.5 text-sm text-blue-100 leading-relaxed">
                All products are supplied strictly for in-vitro laboratory research. Not for human or veterinary use.
              </p>
            </div>
          </div>

          {/* form */}
          <div className="lg:col-span-3">
            <form onSubmit={submit} className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 space-y-5">
              {sent && (
                <div className="flex items-center gap-2 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-700 px-4 py-3 text-sm">
                  <CheckCircle2 className="h-4.5 w-4.5" /> Thanks! Your message has been sent (demo).
                </div>
              )}
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">Full name</label>
                  <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full rounded-lg border border-slate-200 px-3.5 py-2.5 text-sm outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100" placeholder="Dr. Jane Smith" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">Email</label>
                  <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full rounded-lg border border-slate-200 px-3.5 py-2.5 text-sm outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100" placeholder="you@lab.com" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Topic</label>
                <Select value={form.topic} onValueChange={(v) => setForm({ ...form, topic: v })}>
                  <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">General enquiry</SelectItem>
                    <SelectItem value="order">Order &amp; shipping</SelectItem>
                    <SelectItem value="coa">CoA / documentation</SelectItem>
                    <SelectItem value="wholesale">Wholesale &amp; bulk</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Message</label>
                <textarea rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full rounded-lg border border-slate-200 px-3.5 py-2.5 text-sm outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 resize-none" placeholder="How can we help?" />
              </div>
              <button type="submit" className="inline-flex items-center gap-2 rounded-xl bg-blue-600 text-white font-semibold px-6 py-3 hover:bg-blue-700 transition-colors active:scale-[0.99]">
                Send Message <Send className="h-4 w-4" />
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
